import { useEffect, useState, type FormEvent } from "react";
import type { PickupLocation } from "../../../../../api/pickupLocations";
import "../../../modals/Modal.css";
import "../../../components/map/Map.css";
import PickupMap from "../../../components/PickupMap";
import type { DayOption, MapLocation } from "../../../utils/types";

const weekdays = ["monday", "tuesday", "wednesday", "thursday", "friday"];
const LOCKED_PICKUP_LOCATION_ID = "6992d611eb56ff73586183c2";

/** Use with `form={PICKUP_FORM_ID}` on a submit control outside the form tree. */
export const PICKUP_FORM_ID = "pickup-location-form";

const emptyLocation = {
  name: "",
  address: "",
  latitude: 55.6552442,
  longitude: 13.0574175,
};

export type PickupLocationFormData = {
  name: string;
  address: string;
  latitude: number;
  longitude: number;
};

export type PickupLocationSubmitPayload = PickupLocationFormData & {
  days: DayOption[];
};

export type PickupLocationFormProps = {
  activePickup: PickupLocation | undefined;
  /** Called with validated field + day data (create or “next” on edit). */
  onSubmit: (payload: PickupLocationSubmitPayload) => void;
  submitBusy?: boolean;
};

export function PickupLocationForm({
  activePickup,
  onSubmit,
  submitBusy = false,
}: PickupLocationFormProps) {
  const [dataToEdit, setDataToEdit] =
    useState<PickupLocationFormData>(emptyLocation);
  const [selectedDays, setSelectedDays] = useState<DayOption[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const isLockedPickupTitle = activePickup?._id === LOCKED_PICKUP_LOCATION_ID;

  const validationMessage =
    "Please fill out title, location, available days and pickup times!";

  useEffect(() => {
    if (activePickup) {
      setSelectedDays(activePickup.days);
      setDataToEdit({
        name: activePickup.name,
        address: activePickup.address,
        latitude: activePickup.latitude,
        longitude: activePickup.longitude,
      });
    } else {
      setSelectedDays([]);
      setDataToEdit(emptyLocation);
    }
  }, [activePickup]);

  useEffect(() => {
    setFormError(null);
  }, [dataToEdit, selectedDays]);

  function handleNewLocationData(newLocation: MapLocation) {
    setDataToEdit((prev) => ({
      ...prev,
      address: newLocation.address,
      latitude: newLocation.latitude,
      longitude: newLocation.longitude,
    }));
  }

  function handleFormSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitBusy) return;

    const untouchedTime = selectedDays.some(
      (d) => d.startTime === "00:00:00" || d.endTime === "00:00:00",
    );

    if (activePickup) {
      if (dataToEdit.name === "" || selectedDays.length < 1 || untouchedTime) {
        setFormError(validationMessage);
        return;
      }
      setFormError(null);
      onSubmit({ ...dataToEdit, days: selectedDays });
      return;
    }

    if (
      !dataToEdit.name ||
      !dataToEdit.address ||
      selectedDays.length < 1 ||
      untouchedTime
    ) {
      setFormError(validationMessage);
      return;
    }
    setFormError(null);
    onSubmit({ ...dataToEdit, days: selectedDays });
  }

  function getStartTimeForDay(day: string) {
    return selectedDays.find((d) => d.day === day)?.startTime;
  }

  function getEndTimeForDay(day: string) {
    return selectedDays.find((d) => d.day === day)?.endTime;
  }

  function isDayChecked(day: string) {
    return Boolean(selectedDays.find((d) => d.day === day));
  }

  function toggleDay(day: string, value: boolean) {
    setSelectedDays((prev) => {
      const foundDay = prev.find((d) => d.day === day);
      if (!foundDay && value) {
        return [...prev, { day, startTime: "00:00:00", endTime: "00:00:00" }];
      }
      if (foundDay && !value) {
        const i = prev.findIndex((d) => d === foundDay);
        const next = [...prev];
        next.splice(i, 1);
        return next;
      }
      return prev;
    });
  }

  function setStartTime(time: string, day: string) {
    setSelectedDays((prev) =>
      prev.map((d) => (d.day === day ? { ...d, startTime: time } : d)),
    );
  }

  function setEndTime(time: string, day: string) {
    setSelectedDays((prev) =>
      prev.map((d) => (d.day === day ? { ...d, endTime: time } : d)),
    );
  }

  const dayRows = weekdays.map((day) => (
    <label key={day} className="pickup-location-day-options">
      <input
        type="checkbox"
        id={day}
        checked={isDayChecked(day)}
        onChange={(e) => toggleDay(day, e.target.checked)}
      />
      <span className="capitalized">{day}</span>
      {isDayChecked(day) && (
        <div className="pickup-location-time-settings">
          <label>
            <input
              type="time"
              value={getStartTimeForDay(day)}
              onChange={(e) => setStartTime(e.target.value, day)}
            />
          </label>
          {"-"}
          <label>
            <input
              type="time"
              value={getEndTimeForDay(day)}
              onChange={(e) => setEndTime(e.target.value, day)}
            />
          </label>
        </div>
      )}
    </label>
  ));

  return (
    <form
      id={PICKUP_FORM_ID}
      onSubmit={handleFormSubmit}
      className="pickup-modal-wrapper"
    >
      <PickupMap
        locations={activePickup ? [activePickup] : []}
        onLocationSearched={handleNewLocationData}
        clickableMarker={true}
      />

      <div className="info-card info-card-margin-top">
        <div className="title-input">
          <label htmlFor="pickup-title" className="sr-only">
            Title
          </label>
          <input
            className="location-title-input"
            type="text"
            id="pickup-title"
            name="PickupTitle"
            value={dataToEdit.name}
            placeholder="Pickup Title"
            onChange={(e) => {
              const name = e.currentTarget.value;
              setDataToEdit((prev) => ({ ...prev, name }));
            }}
            disabled={isLockedPickupTitle}
          />
        </div>
        <p>Days of the week this location is available for pickup:</p>
        <div className="weekdays ">{dayRows}</div>
      </div>
      {formError && (
        <p className="pickup-form-validation-msg" role="alert">
          {formError}
        </p>
      )}
    </form>
  );
}
